import { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";

import {
    Box,
    Typography,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from "@mui/material";

const CustomFormDialog = ({
    title,
    fields,
    open,
    onClose,
    formSubmitText = "SUBMIT",
    handleSubmitClick,
}) => {
    console.log({ fields });
    const initializeFields = (fieldsArray) => {
        const initialValues = {};
        fieldsArray.forEach((field) => {
            if (field.type === "checkbox") {
                initialValues[field.name] = field.checked || false;
            } else if (field.type === "radio" || field.type === "select") {
                const selectedOption = field.options.find(
                    (option) => option.selected,
                );
                initialValues[field.name] =
                    selectedOption?.value ||
                    field.options[0]?.value ||
                    field.defaultValue ||
                    "";
            } else {
                initialValues[field.name] =
                    field.value || field.defaultValue || "";
            }
        });
        return initialValues;
    };

    const [inputFields, setInputFields] = useState(() =>
        initializeFields(fields),
    );
    const [inputErrors, setInputErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false); // Added state for loading

    useEffect(() => {
        setInputFields(initializeFields(fields));
    }, [fields]);
    // Default validation logic for different field types
    const defaultValidation = (field, value) => {
        if (field.required && (value === "" || value === undefined)) {
            return `${field.label} is required`;
        }
        if (field.type === "email") {
            return /\S+@\S+\.\S+/.test(value) ? "" : "Invalid email format";
        }
        if (field.type === "number") {
            return isNaN(value) ||
                (typeof value === "string" && value.trim() === "")
                ? "Must be a valid number"
                : "";
        }
        return "";
    };

    const handleFieldValidation = (e) => {
        const { name, value, type, checked } = e.target;
        const actualValue = type === "checkbox" ? checked : value;
        console.log(value);

        const field = fields.find((f) => f.name === name);
        if (field.onFieldChange) {
            const updatedFields = field.onFieldChange(
                { ...inputFields, [name]: actualValue },
                actualValue,
            );
            setInputFields(updatedFields);
        } else {
            setInputFields({ ...inputFields, [name]: actualValue });
        }
        // Validate field
        setInputErrors((prevErrors) => ({
            ...prevErrors,
            [name]: field.validate
                ? field.validate(actualValue)
                : defaultValidation(field, actualValue),
        }));
    };

    const validateForm = () => {
        let isValid = true;
        const newErrors = {};

        fields.forEach((field) => {
            const value = inputFields[field.name];
            const error = field.validate
                ? field.validate(value)
                : defaultValidation(field, value);

            newErrors[field.name] = error;
            if (error) isValid = false;
        });

        setInputErrors(newErrors);
        return isValid;
    };

    const handleSubmitForm = async () => {
        if (validateForm()) {
            setIsSubmitting(true); // Set loading state to true
            await handleSubmitClick(inputFields); // Pass validated form data
            setIsSubmitting(false); // Reset loading state
            handleReset();
        }
    };
    const handleReset = () => {
        setInputFields(initializeFields(fields));
    };
    const handleClose = () => {
        onClose();
    };

    const renderField = (field, theme) => {
        switch (field.type) {
            case "text":
            case "email":
            case "number":
                return (
                    <TextField
                        dir={theme.direction === "rtl" ? "rtl" : "ltr"}
                        key={field.name}
                        label={field.label}
                        name={field.name}
                        type={field.type}
                        value={inputFields[field.name] || ""}
                        onChange={handleFieldValidation}
                        variant="outlined"
                        size="small"
                        margin="dense"
                        error={!!inputErrors[field.name]}
                        helperText={inputErrors[field.name]}
                        fullWidth
                        disabled={isSubmitting} // Disable input while submitting
                    />
                );
            case "select":
                return (
                    <FormControl
                        fullWidth
                        margin="dense"
                        key={field.name}
                        dir={theme.direction === "rtl" ? "rtl" : "ltr"}
                        disabled={isSubmitting} // Disable input while submitting
                    >
                        <InputLabel size="small">{field.label}</InputLabel>
                        <Select
                            name={field.name}
                            value={inputFields[field.name] || ""}
                            onChange={handleFieldValidation}
                            size="small"
                            label={field.label}
                        >
                            {field.options.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                    disabled={option?.disabled}
                                    selected={option?.selected}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {inputErrors[field.name] && (
                            <Typography color="error" variant="caption">
                                {inputErrors[field.name]}
                            </Typography>
                        )}
                    </FormControl>
                );
            case "radio":
                return (
                    <FormControl
                        component="fieldset"
                        key={field.name}
                        dir={theme.direction === "rtl" ? "rtl" : "ltr"}
                    >
                        <FormLabel>{field.label}</FormLabel>
                        <RadioGroup
                            name={field.name}
                            value={inputFields[field.name] || ""}
                            onChange={handleFieldValidation}
                        >
                            {field.options.map((option) => (
                                <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={<Radio />}
                                    label={option.label}
                                />
                            ))}
                        </RadioGroup>
                        {inputErrors[field.name] && (
                            <Typography color="error" variant="caption">
                                {inputErrors[field.name]}
                            </Typography>
                        )}
                    </FormControl>
                );
            case "checkbox":
                return (
                    <FormControlLabel
                        dir={theme.direction === "rtl" ? "rtl" : "ltr"}
                        key={field.name}
                        control={
                            <Checkbox
                                name={field.name}
                                checked={inputFields[field.checked] || false}
                                onChange={handleFieldValidation}
                                disabled={isSubmitting} // Disable input while submitting
                            />
                        }
                        label={field.label}
                    />
                );
            case "hidden":
                return (
                    <input
                        type="hidden"
                        name={field.name}
                        value={inputFields[field.name] || ""}
                    />
                );
            default:
                return null;
        }
    };
    const theme = useTheme();

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth={true}
            dir={theme.direction === "rtl" ? "rtl" : "ltr"}
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                }}
            >
                {isSubmitting && (
                    <Typography variant="body2" color="textSecondary">
                        Submitting, please wait...
                    </Typography>
                )}
                {fields.map((field) => (
                    <Box
                        key={field.name}
                        mb={2}
                        sx={{ width: field.width || "100%" }}
                    >
                        {renderField(field, theme)}
                    </Box>
                ))}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    variant="text"
                    color="error"
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmitForm}
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting} // Disable button while submitting
                >
                    {formSubmitText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CustomFormDialog;
